import React, { useState, useEffect, useContext } from "react";
import {
  FaBars,
  FaSignOutAlt,
} from "react-icons/fa";
import {
  MdOutlineMenuBook,
  MdOutlinePerson,
  MdTimeline,
  MdOutlineTaskAlt,
  // MdOutlineChat,
} from "react-icons/md";
import { UserContext } from "../../../UserContext";
import { apiFetch } from "../../../services/apiFetch";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

/**
 * Helper to load modules for the first course
 */
const fetchCourseModules = async (userId) => {
  if (!userId) return [];

  try {
    const coursesRes = await apiFetch(`${apiBaseURL}/courses`);
    if (!coursesRes.ok) return [];
    
    const courses = await coursesRes.json();
    if (!Array.isArray(courses) || courses.length === 0) return [];

    const firstCourse = courses[0];

    const progressRes = await apiFetch(
      `${apiBaseURL}/users/${userId}/courses/${firstCourse.id}/progress`
    );
    
    if (!progressRes.ok) return [];
    
    const progress = await progressRes.json();
    if (!progress?.modules?.length) return [];

    return progress.modules;
  } catch (error) {
    console.error("Error loading course progress:", error);
    return [];
  }
};

const isSubsectionCompleted = (sub) =>
  sub?.completed === true ||
  sub?.completed === 1 ||
  sub?.completed === "true" ||
  sub?.status === "completed";

const isModuleCompletedByOrder = (modules, order) => {
  if (!Array.isArray(modules)) return false;
  const moduleItem = modules.find((m) => m.order === order);
  if (!moduleItem || !moduleItem.subsections?.length) return false;

  const allCompleted = moduleItem.subsections.every(isSubsectionCompleted);

  console.log(`Module ${order} Check:`, {
    totalSubsections: moduleItem.subsections.length,
        completedSubsections: moduleItem.subsections.filter(isSubsectionCompleted)
          .length,
    allCompleted,
  });

  return allCompleted;
};

const Sidebar = ({ sidebarOpen, onOpen, onClose, currentPage, onNavigate }) => {
  const { user } = useContext(UserContext);
  const userId = user?.user_id;

  const [showLockedModal, setShowLockedModal] = useState(false);
  const [hasModule1Completed, setHasModule1Completed] = useState(false);
  const [hasModule4Completed, setHasModule4Completed] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [pendingNavigationId, setPendingNavigationId] = useState(null);

  // Check Module 1 completion on mount and when currentPage changes (in case user completed module)
  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      if (!userId) {
        if (isMounted) {
          setIsCheckingAccess(false);
          setHasModule1Completed(false);
        }
        return;
      }

      setIsCheckingAccess(true);
      const modules = await fetchCourseModules(userId);
      const module1Completed = isModuleCompletedByOrder(modules, 1);
      const module4Completed = isModuleCompletedByOrder(modules, 4);
      
      if (isMounted) {
        setHasModule1Completed(module1Completed);
        setHasModule4Completed(module4Completed);
        setIsCheckingAccess(false);
        console.log("Module completion status:", {
          module1Completed,
          module4Completed,
        });
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [userId, currentPage]); // Re-check when currentPage changes

  const menuItems = [
    {
      id: "learning",
      label: "Learning Path",
      icon: <MdOutlineMenuBook />,
    },
    {
      id: "space",
      label: "My Space",
      icon: <MdOutlinePerson />,
      lockedBy: "module1",
    },
    {
      id: "journey",
      label: "My Journey",
      icon: <MdTimeline />,
      lockedBy: "module4",
    },
    {
      id: "action",
      label: "My Action Plan",
      icon: <MdOutlineTaskAlt />,
      lockedBy: "module1",
    },
  ];

  const isItemLocked = (item) => {
    if (!item?.lockedBy) return false;
    if (item.lockedBy === "module1") return !hasModule1Completed;
    if (item.lockedBy === "module4") return !hasModule4Completed;
    return false;
  };

  const getLockDetails = (itemId) => {
    if (itemId === "journey") {
      return {
        moduleOrder: 4,
        label: "My Journey",
        lockCopy: 'Complete modules 1 to 4 of your learning path to unlock "My Journey".',
      };
    }
    if (itemId === "action") {
      return { moduleOrder: 1, label: "My Action Plan" };
    }
    return { moduleOrder: 1, label: "My Space" };
  };

  const handleNavigationClick = (itemId) => {
    const menuItem = menuItems.find((item) => item.id === itemId);

    // Check if route is locked
    const shouldShowModal = isItemLocked(menuItem) && !isCheckingAccess;

    if (shouldShowModal) {
      console.log("Route locked, showing modal:", itemId);
      setPendingNavigationId(itemId);
      setShowLockedModal(true);
      return;
    }

    // Navigate normally
    console.log("Navigating to:", itemId);
    onNavigate(itemId);
    onClose();
  };

  const handleModalClose = () => {
    setShowLockedModal(false);
    setPendingNavigationId(null);
  };

  const handleGoToLearning = () => {
    setShowLockedModal(false);
    setPendingNavigationId(null);
    onNavigate("learning");
    onClose();
  };

  return (
    <>
      {/* Sidebar */}
      <div
        className={`
          absolute left-[32px] top-[25px] h-[805px]
          bg-[#042a2b] text-white rounded-2xl
          shadow-lg flex flex-col
          transition-all duration-300 ease-in-out z-40
          ${sidebarOpen ? "w-[300px]" : "w-[86px]"}
        `}
      >
        {/* Header */}
        <div className="flex items-center px-4 py-6">
          {!sidebarOpen ? (
            <button
              onClick={onOpen}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-white/10 ml-1.5 hover:bg-white/20"
            >
              <FaBars />
            </button>
          ) : (
            <>
              <span className="text-lg font-semibold ml-1">Stoc.ai</span>
              <button
                onClick={onClose}
                className="ml-auto text-white/60 hover:text-white"
              >
                ✕
              </button>
            </>
          )}
        </div>

        {/* Menu */}
        <nav className="flex-1 px-3 py-4 space-y-2">
          {menuItems.map((item) => {
            const isActive = currentPage && currentPage === item.id;
            const isLocked = isItemLocked(item);

            return (
              <div key={item.id} className="relative">
                <button
                  onClick={() => handleNavigationClick(item.id)}
                  title={!sidebarOpen ? item.label : undefined}
                  className={`
                    flex items-center w-full px-4 py-3 rounded-xl
                    transition
                    ${
                      isActive
                        ? "bg-[#5eb1bf] text-[#042a2b]"
                        : "text-white hover:bg-white/10"
                    }
                  `}
                >
                  <span className="text-xl min-w-[24px] flex justify-center">
                    {item.icon}
                  </span>
                  {sidebarOpen && (
                    <span className="ml-4 font-medium flex items-center gap-2">
                      {item.label}
                      {isLocked && !isCheckingAccess && (
                        <svg
                          className="w-4 h-4 text-white/60"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM8.9 6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H8.9V6zM18 20H6V10h12v10z" />
                        </svg>
                      )}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-3 pb-6 mt-auto">
          <button
            onClick={() => {
              onNavigate("logout");
              onClose();
            }}
            title={!sidebarOpen ? "Logout" : undefined}
            className="flex items-center w-full px-4 py-3 rounded-xl text-white hover:bg-white/10 transition"
          >
            <span className="text-xl min-w-[24px] flex justify-center">
              <FaSignOutAlt />
            </span>
            {sidebarOpen && <span className="ml-4">Logout</span>}
          </button>
        </div>
      </div>

      {/* Dim background */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={onClose}
        />
      )}

      {/* Module Locked Modal */}
      {showLockedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            {/* Close Button */}
            <div className="flex justify-end">
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="text-center mt-4">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-[#54B0AF] bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-[#54B0AF]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-medium font-gotham text-[#1D1D1D] mb-3">
                {pendingNavigationId === "journey"
                  ? "Complete Modules 1-4 to Unlock"
                  : "Complete Module 1 to Unlock"}
              </h2>

              <p className="text-base text-gray-600 font-gotham mb-6">
                {pendingNavigationId && (() => {
                  const { moduleOrder, label, lockCopy } = getLockDetails(
                    pendingNavigationId
                  );
                  if (lockCopy) return lockCopy;
                  return `Complete module ${moduleOrder} of your learning path to unlock "${label}".`;
                })()}
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGoToLearning}
                  className="w-full bg-black text-white py-3 px-6 rounded-lg font-medium font-gotham hover:bg-gray-800 transition-colors"
                >
                  Go to Learning Path
                </button>
{/* 
                <button
                  onClick={handleModalClose}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium font-gotham hover:bg-gray-200 transition-colors"
                >
                  Maybe Later
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
